import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Badge,
  IconButton,
  Typography,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  Collapse,
  Paper
} from '@mui/material';
import {
  LocalOffer as TagIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

const TagFilter = ({
  selectedTags = [],
  onTagsChange,
  tagMode = 'OR',
  onTagModeChange,
  disabled = false,
  languageCode = null
}) => {
  const [open, setOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [tempSelectedTags, setTempSelectedTags] = useState([]);
  const [tempTagMode, setTempTagMode] = useState('OR');

  useEffect(() => {
    loadTags();
  }, [languageCode]);

  useEffect(() => {
    // Filter tags based on search
    if (searchTerm) {
      const filtered = allTags.filter(tag =>
        tag.tag_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags(allTags);
    }
  }, [searchTerm, allTags]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await apiClient.tags.getUserTags(languageCode);
      const tags = response.data.tags || [];
      setAllTags(tags);
      setFilteredTags(tags);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setTempSelectedTags([...selectedTags]);
    setTempTagMode(tagMode);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleApply = () => {
    onTagsChange(tempSelectedTags);
    if (onTagModeChange) {
      onTagModeChange(tempTagMode);
    }
    setOpen(false);
  };

  const handleToggleTag = (tag) => {
    const isSelected = tempSelectedTags.some(t => t.id === tag.id);

    if (isSelected) {
      setTempSelectedTags(tempSelectedTags.filter(t => t.id !== tag.id));
    } else {
      setTempSelectedTags([...tempSelectedTags, tag]);
    }
  };

  const handleClearAll = () => {
    setTempSelectedTags([]);
  };

  const handleQuickToggle = (tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    let newTags;

    if (isSelected) {
      newTags = selectedTags.filter(t => t.id !== tag.id);
    } else {
      newTags = [...selectedTags, tag];
    }

    onTagsChange(newTags);
  };

  const handleClearFilters = () => {
    onTagsChange([]);
  };

  // Get top tags for quick access (when not expanded)
  const topTags = allTags.slice(0, 5);

  return (
    <Box>
      {/* Inline Filter Display */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TagIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">Tag Filters</Typography>
            {selectedTags.length > 0 && (
              <Chip
                size="small"
                label={`${selectedTags.length} selected`}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {selectedTags.length > 0 && (
              <IconButton size="small" onClick={handleClearFilters}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Selected Tags Display */}
        {selectedTags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {selectedTags.map(tag => (
              <Chip
                key={tag.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tag.tag_name}
                    {tag.word_count > 0 && (
                      <Typography variant="caption" component="span" sx={{ opacity: 0.7 }}>
                        ({tag.word_count})
                      </Typography>
                    )}
                  </Box>
                }
                size="small"
                onDelete={() => handleQuickToggle(tag)}
                sx={{
                  backgroundColor: tag.color ? `${tag.color}20` : undefined,
                  borderColor: tag.color || 'divider',
                }}
              />
            ))}
          </Box>
        )}

        {/* Quick Filter Tags */}
        <Collapse in={expanded}>
          <Box>
            {selectedTags.length > 0 && onTagModeChange && (
              <Box sx={{ mb: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  value={tagMode}
                  exclusive
                  onChange={(e, newMode) => newMode && onTagModeChange(newMode)}
                >
                  <ToggleButton value="OR">
                    <Typography variant="caption">Any of these tags (OR)</Typography>
                  </ToggleButton>
                  <ToggleButton value="AND">
                    <Typography variant="caption">All of these tags (AND)</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {topTags
                .filter(tag => !selectedTags.some(t => t.id === tag.id))
                .map(tag => (
                  <Chip
                    key={tag.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {tag.tag_name}
                        {tag.word_count > 0 && (
                          <Typography variant="caption" component="span" sx={{ opacity: 0.7 }}>
                            ({tag.word_count})
                          </Typography>
                        )}
                      </Box>
                    }
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickToggle(tag)}
                    sx={{
                      borderColor: tag.color || 'divider',
                      '&:hover': {
                        backgroundColor: tag.color ? `${tag.color}20` : 'action.hover',
                      }
                    }}
                  />
              ))}

              <Button
                size="small"
                startIcon={<FilterIcon />}
                onClick={handleOpen}
                variant="outlined"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  minWidth: 'auto',
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
              >
                All Tags
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Full Tags Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Filter by Tags</Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Tag Mode Toggle */}
          {tempSelectedTags.length > 0 && onTagModeChange && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Filter Mode
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={tempTagMode}
                exclusive
                onChange={(e, newMode) => newMode && setTempTagMode(newMode)}
                fullWidth
              >
                <ToggleButton value="OR">
                  Any of these tags
                </ToggleButton>
                <ToggleButton value="AND">
                  All of these tags
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Tags List */}
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {filteredTags.map(tag => {
              const isSelected = tempSelectedTags.some(t => t.id === tag.id);

              return (
                <ListItemButton
                  key={tag.id}
                  onClick={() => handleToggleTag(tag)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {tag.color && (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: tag.color,
                            }}
                          />
                        )}
                        <Typography variant="body2">
                          {tag.tag_name}
                        </Typography>
                        {tag.is_default && (
                          <Chip label="default" size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={`${tag.word_count || 0} words`}
                  />
                </ListItemButton>
              );
            })}
          </List>

          {tempSelectedTags.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                onClick={handleClearAll}
                startIcon={<ClearIcon />}
              >
                Clear All ({tempSelectedTags.length})
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagFilter;